/**
 * Mailchimp Adapter (Story 12.1, Task 5 + IMC-18 expansion)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * Key Mailchimp specifics:
 * - Data center from provider_account_id: "{dc}:{user_id}" → base URL https://{dc}.api.mailchimp.com/3.0/
 * - Auth header: "Bearer {token}" for API calls
 * - No refresh token — access tokens are permanent
 * - Rate limit: 10 simultaneous connections (concurrency-based, not per-minute)
 * - 429 responses trigger retry/backoff
 *
 * IMC-18: Expanded from 19 to 76 metrics, 5 to 15 dimensions.
 * New record types: ecommerce, productActivity, automation, automationStep,
 *   clickMap, domainPerformance, locations, dailyActivity
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { parseMailchimpApiError } from "./errors";
import {
  buildMailchimpApiBaseUrl,
  fetchMailchimpAccountInfoWithDetail,
} from "./discovery";

// ============================================================================
// Constants
// ============================================================================

/** Max campaigns to fetch per request (Mailchimp API per-page limit) */
const MAX_CAMPAIGNS = 100;

/** API request timeout */
const API_TIMEOUT_MS = 25_000;

/** Batch size for concurrent API requests (respects 10-connection limit) */
const BATCH_SIZE = 5;

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface MailchimpListStatsResponse {
  id: string;
  name: string;
  list_rating: number;
  stats: {
    member_count: number;
    unsubscribe_count: number;
    cleaned_count: number;
    member_count_since_send: number;
    unsubscribe_count_since_send: number;
    cleaned_count_since_send: number;
    open_rate: number;
    click_rate: number;
    last_sub_date: string;
    last_unsub_date: string;
  };
}

interface MailchimpCampaign {
  id: string;
  type: string;
  create_time: string;
  send_time: string;
  settings: {
    subject_line: string;
    title: string;
  };
  recipients: {
    list_id: string;
    recipient_count: number;
  };
  report_summary?: {
    opens: number;
    unique_opens: number;
    open_rate: number;
    clicks: number;
    subscriber_clicks: number;
    click_rate: number;
  };
  variate_settings?: {
    winning_metric?: string;
    winning_campaign_id?: string;
  };
}

interface MailchimpSubReportsResponse {
  campaigns: MailchimpCampaignReport[];
}

interface MailchimpCampaignsResponse {
  campaigns: MailchimpCampaign[];
  total_items: number;
}

interface MailchimpCampaignReport {
  id: string;
  campaign_title: string;
  subject_line: string;
  emails_sent: number;
  unsubscribed: number;
  abuse_reports: number;
  forwards_count: number;
  opens: {
    opens_total: number;
    unique_opens: number;
    open_rate: number;
  };
  clicks: {
    clicks_total: number;
    unique_clicks: number;
    unique_subscriber_clicks: number;
    click_rate: number;
  };
  bounces: {
    hard_bounces: number;
    soft_bounces: number;
    syntax_errors: number;
  };
  send_time: string;
}

interface MailchimpGrowthHistoryEntry {
  month: string;
  existing: number;
  imports: number;
  optins: number;
  reconfirm: number;
  cleaned: number;
  pending: number;
  /** @deprecated Returns 0 since Aug 2020 — use subscribes instead */
  subscribes?: number;
}

interface MailchimpGrowthHistoryResponse {
  growth_history: MailchimpGrowthHistoryEntry[];
  total_items: number;
}

// ---- IMC-18: New response types for expanded endpoints ----

interface MailchimpEcommerceResponse {
  total_revenue: number;
  total_orders: number;
  average_order_revenue: number;
  total_spent: number;
  currency_code: string;
}

interface MailchimpEcommerceProduct {
  title: string;
  sku: string;
  total_revenue: number;
  total_purchased: number;
  recommendation_purchased: number;
}

interface MailchimpEcommerceProductResponse {
  products: MailchimpEcommerceProduct[];
  total_items: number;
}

interface MailchimpClickDetailUrl {
  id: string;
  url: string;
  total_clicks: number;
  unique_clicks: number;
  click_percentage: number;
}

interface MailchimpClickDetailsResponse {
  urls_clicked: MailchimpClickDetailUrl[];
  total_items: number;
}

interface MailchimpDomainPerformanceEntry {
  domain: string;
  emails_sent: number;
  bounces: number;
  opens: number;
  clicks: number;
  unsubs: number;
  delivered: number;
  emails_pct: number;
  bounces_pct: number;
  opens_pct: number;
  clicks_pct: number;
  unsubs_pct: number;
}

interface MailchimpDomainPerformanceResponse {
  domains: MailchimpDomainPerformanceEntry[];
  total_items: number;
}

interface MailchimpLocationEntry {
  country_code: string;
  region: string;
  opens: number;
  percent_opens: number;
}

interface MailchimpLocationsResponse {
  locations: MailchimpLocationEntry[];
}

interface MailchimpListLocationEntry {
  country: string;
  cc: string;
  percent: number;
  total: number;
}

interface MailchimpListLocationsResponse {
  locations: MailchimpListLocationEntry[];
}

interface MailchimpAutomationReportSummary {
  opens: number;
  unique_opens: number;
  open_rate: number;
  clicks: number;
  subscriber_clicks: number;
  click_rate: number;
}

interface MailchimpAutomation {
  id: string;
  settings: { title: string };
  emails_sent: number;
  report_summary: MailchimpAutomationReportSummary;
}

interface MailchimpAutomationsResponse {
  automations: MailchimpAutomation[];
  total_items: number;
}

interface MailchimpAutomationEmail {
  id: string;
  position: number;
  settings: { subject_line: string; title: string };
  emails_sent: number;
  report_summary: MailchimpAutomationReportSummary;
}

interface MailchimpAutomationEmailsResponse {
  emails: MailchimpAutomationEmail[];
  total_items: number;
}

interface MailchimpDailyActivityEntry {
  day: string;
  emails_sent: number;
  unique_opens: number;
  recipient_clicks: number;
  hard_bounces: number;
  soft_bounces: number;
  subs: number;
  unsubs: number;
  other_adds: number;
  other_removes: number;
}

interface MailchimpDailyActivityResponse {
  activity: MailchimpDailyActivityEntry[];
  total_items: number;
}

/** Extended campaign report with industry_stats, forwards, and ab_split */
interface MailchimpFullCampaignReport extends MailchimpCampaignReport {
  industry_stats?: {
    type: string;
    open_rate: number;
    click_rate: number;
    bounce_rate: number;
    unsub_rate: number;
    abuse_rate: number;
  };
  forwards?: {
    forwards_count: number;
    forwards_opens: number;
  };
  ab_split?: {
    a?: MailchimpAbSplitVariant;
    b?: MailchimpAbSplitVariant;
  };
  list_id?: string;
}

interface MailchimpAbSplitVariant {
  opens: number;
  unique_opens: number;
  recipient_clicks: number;
  bounces: number;
  unsubs: number;
  forwards: number;
}

// ============================================================================
// Helper: Parse dc from provider_account_id
// ============================================================================

function parseDcFromAccountId(providerAccountId: string): { dc: string; accountId: string } {
  const parts = providerAccountId.split(":");
  const dc = parts[0] || "";
  const accountId = parts.slice(1).join(":");
  return { dc, accountId };
}

// ============================================================================
// Metric & Dimension Definitions (per Integration Mapping Reference)
// ============================================================================

export const MAILCHIMP_METRICS: MetricDefinition[] = [
  // ---- Growth (9) ----
  { key: "total_subscribers", label: "Total Subscribers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart", "goal"] },
  { key: "new_subscribers", label: "New Subscribers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "unsubscribes", label: "Unsubscribes", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "unsubscribe_count", label: "Unsubscribe Count", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cleaned_count", label: "Cleaned Count", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "reconfirms", label: "Reconfirms", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cleaned_monthly", label: "Cleaned (Monthly)", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "pending", label: "Pending", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "list_rating", label: "List Rating", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"] },

  // ---- Delivery (6) ----
  { key: "campaigns_sent", label: "Campaigns Sent", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "emails_sent", label: "Emails Sent", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "recipients_count", label: "Recipients", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart", "data-table"] },
  { key: "delivery_rate", label: "Delivery Rate", type: "percentage", category: "Delivery", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "successful_deliveries", denominator: "emails_sent" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "successful_deliveries", label: "Successful Deliveries", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "abuse_reports", label: "Abuse Reports", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ---- Email Opens (5) ----
  { key: "unique_opens", label: "Unique Opens", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "total_opens", label: "Total Opens", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "open_rate", label: "Open Rate (Campaign)", type: "percentage", category: "Email", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unique_opens", denominator: "successful_deliveries" }, compatibleWidgets: ["kpi", "gauge", "data-table"] },
  { key: "average_open_rate", label: "Average Open Rate", type: "percentage", category: "Email", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "successful_deliveries" }, compatibleWidgets: ["kpi", "line-chart", "gauge", "goal"] },
  { key: "open_count", label: "Open Count", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart", "data-table"] },

  // ---- Email Clicks (7) ----
  { key: "unique_clicks", label: "Unique Clicks", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "total_clicks", label: "Total Clicks", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "click_rate", label: "Click Rate (Campaign)", type: "percentage", category: "Email", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unique_clicks", denominator: "successful_deliveries" }, compatibleWidgets: ["kpi", "gauge", "data-table"] },
  { key: "average_click_rate", label: "Average Click Rate", type: "percentage", category: "Email", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "successful_deliveries" }, compatibleWidgets: ["kpi", "line-chart", "gauge", "goal"] },
  { key: "click_to_open_rate", label: "Click-to-Open Rate", type: "percentage", category: "Email", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unique_clicks", denominator: "unique_opens" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "subscriber_clicks", label: "Subscriber Clicks", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "click_count", label: "Click Count", type: "number", category: "Email", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart", "data-table"] },

  // ---- Bounces (4) ----
  { key: "hard_bounces", label: "Hard Bounces", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "soft_bounces", label: "Soft Bounces", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"] },
  { key: "bounce_rate", label: "Bounce Rate", type: "percentage", category: "Bounces", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "emails_sent" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "syntax_errors", label: "Syntax Errors", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ---- Unsubscribes (2) ----
  { key: "unsubscribe_rate", label: "Unsubscribe Rate", type: "percentage", category: "Unsubscribes", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unsubscribes", denominator: "emails_sent" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "abuse_rate", label: "Abuse Rate", type: "percentage", category: "Unsubscribes", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "abuse_reports", denominator: "emails_sent" }, compatibleWidgets: ["kpi"] },

  // ---- Forwards (2) ----
  { key: "forwards", label: "Forwards", type: "number", category: "Forwards", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "forward_opens", label: "Forward Opens", type: "number", category: "Forwards", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ---- E-commerce Revenue (5) ----
  { key: "revenue", label: "Revenue", type: "currency", category: "E-commerce", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "orders", label: "Orders", type: "number", category: "E-commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "avg_order_revenue", label: "Avg Order Revenue", type: "currency", category: "E-commerce", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "revenue", denominator: "orders" }, compatibleWidgets: ["kpi"] },
  { key: "total_spent", label: "Total Spent", type: "currency", category: "E-commerce", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "order_rate", label: "Order Rate", type: "percentage", category: "E-commerce", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "orders", denominator: "emails_sent" }, compatibleWidgets: ["kpi"] },

  // ---- E-commerce Product Activity (3) ----
  { key: "product_revenue", label: "Product Revenue", type: "currency", category: "E-commerce", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "product_units_purchased", label: "Product Units Purchased", type: "number", category: "E-commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "product_recommendations_purchased", label: "Product Recommendations Purchased", type: "number", category: "E-commerce", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },

  // ---- URL Click Map (3) ----
  { key: "url_total_clicks", label: "URL Total Clicks", type: "number", category: "Click Map", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "url_unique_clicks", label: "URL Unique Clicks", type: "number", category: "Click Map", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "url_click_percentage", label: "URL Click Percentage", type: "percentage", category: "Click Map", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "url_total_clicks", denominator: "total_clicks" }, compatibleWidgets: ["data-table"] },

  // ---- Domain Performance (6) ----
  { key: "domain_emails_sent", label: "Domain Emails Sent", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "domain_opens", label: "Domain Opens", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "domain_clicks", label: "Domain Clicks", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "domain_bounces", label: "Domain Bounces", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "domain_delivered", label: "Domain Delivered", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "domain_unsubs", label: "Domain Unsubscribes", type: "number", category: "Domain", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },

  // ---- Location Reports (2) ----
  { key: "location_opens", label: "Location Opens", type: "number", category: "Location", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"] },
  { key: "location_percent_opens", label: "Location Percent Opens", type: "percentage", category: "Location", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "location_opens", denominator: "total_opens" }, compatibleWidgets: ["data-table"] },

  // ---- Classic Automation (6) ----
  { key: "automation_opens", label: "Automation Opens", type: "number", category: "Automation", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "automation_unique_opens", label: "Automation Unique Opens", type: "number", category: "Automation", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "automation_open_rate", label: "Automation Open Rate", type: "percentage", category: "Automation", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi"] },
  { key: "automation_clicks", label: "Automation Clicks", type: "number", category: "Automation", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "automation_subscriber_clicks", label: "Automation Subscriber Clicks", type: "number", category: "Automation", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "automation_click_rate", label: "Automation Click Rate", type: "percentage", category: "Automation", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi"] },

  // ---- A/B Test Variants (6) ----
  { key: "variant_opens", label: "Variant Opens", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "variant_unique_opens", label: "Variant Unique Opens", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "variant_clicks", label: "Variant Clicks", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "variant_bounces", label: "Variant Bounces", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "variant_unsubs", label: "Variant Unsubscribes", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },
  { key: "variant_forwards", label: "Variant Forwards", type: "number", category: "A/B Test", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"] },

  // ---- Industry Benchmarks (5) ----
  { key: "industry_open_rate", label: "Industry Open Rate", type: "percentage", category: "Benchmark", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["benchmark"] },
  { key: "industry_click_rate", label: "Industry Click Rate", type: "percentage", category: "Benchmark", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["benchmark"] },
  { key: "industry_bounce_rate", label: "Industry Bounce Rate", type: "percentage", category: "Benchmark", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["benchmark"] },
  { key: "industry_unsub_rate", label: "Industry Unsub Rate", type: "percentage", category: "Benchmark", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["benchmark"] },
  { key: "industry_abuse_rate", label: "Industry Abuse Rate", type: "percentage", category: "Benchmark", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["benchmark"] },

  // ---- Daily Activity (5) ----
  { key: "daily_emails_sent", label: "Daily Emails Sent", type: "number", category: "Daily Activity", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"] },
  { key: "daily_unique_opens", label: "Daily Unique Opens", type: "number", category: "Daily Activity", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"] },
  { key: "daily_recipient_clicks", label: "Daily Recipient Clicks", type: "number", category: "Daily Activity", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"] },
  { key: "daily_subs", label: "Daily Subs", type: "number", category: "Daily Activity", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"] },
  { key: "daily_unsubs", label: "Daily Unsubs", type: "number", category: "Daily Activity", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"] },
];

export const MAILCHIMP_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time" },
  { key: "campaign_name", label: "Campaign Name", type: "Content" },
  { key: "campaign_subject", label: "Subject Line", type: "Content" },
  { key: "list_name", label: "Audience/List", type: "Audience" },
  { key: "campaign_type", label: "Campaign Type", type: "Content" },
  // IMC-18: New dimensions
  { key: "url", label: "URL (Click Map)", type: "Content" },
  { key: "email_domain", label: "Email Domain", type: "Content" },
  { key: "country", label: "Country", type: "Geo" },
  { key: "region", label: "Region", type: "Geo" },
  { key: "subscriber_country", label: "Subscriber Country", type: "Geo" },
  { key: "automation_workflow", label: "Automation Workflow", type: "Content" },
  { key: "automation_step", label: "Automation Step", type: "Content" },
  { key: "ab_variant", label: "A/B Variant", type: "Content" },
  { key: "product", label: "Product (E-commerce)", type: "Content" },
  { key: "timeseries_hour", label: "Hourly Timeseries", type: "Time" },
];

// ============================================================================
// Adapter Implementation
// ============================================================================

export const mailchimpAdapter: IntegrationAdapter = {
  platform: "mailchimp",

  rateLimits: {
    requestsPerMinute: 600, // Concurrency-based (10 simultaneous), not rate-based
    retryAfterMs: 1000,
  },

  // Mailchimp tokens are permanent — no refreshToken() method needed.

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Parse dc from accountId (stored as provider_account_id in oauth_tokens)
    // The accountId in integration row may be an audience ID (e.g., "list_abc123")
    // but we need the dc from the oauth token's provider_account_id
    // For now, extract from the env or context — the route layer passes it via accountId
    const providerAccountId = (ctx.env as unknown as Record<string, string>).providerAccountId || "";
    if (!providerAccountId.includes(":")) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid provider_account_id format: expected {dc}:{user_id}",
        retryable: false,
      });
    }

    const { dc } = parseDcFromAccountId(providerAccountId);
    let baseUrl: string;
    try {
      baseUrl = buildMailchimpApiBaseUrl(dc);
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: `Invalid Mailchimp data center prefix: "${dc}"`,
        retryable: false,
      });
    }
    const token = credentials.accessToken;
    const headers = { Authorization: `Bearer ${token}` };

    // Audience list ID from the integration's accountId field
    const audienceId = ctx.accountId || "";

    const startISO = dateRange.start.toISOString();
    const endISO = dateRange.end.toISOString();

    // ---- Fetch list stats (audience overview) ----
    let listStats: MailchimpListStatsResponse | null = null;
    if (audienceId) {
      const listResult = await safeFetch(
        `${baseUrl}/lists/${audienceId}?fields=id,name,stats,list_rating`,
        { method: "GET", headers },
        API_TIMEOUT_MS
      );
      if (listResult.success && listResult.data.ok) {
        listStats = (await listResult.data.json()) as MailchimpListStatsResponse;
      }
    }

    // ---- Fetch campaigns in date range ----
    const campaignsUrl = `${baseUrl}/campaigns?count=${MAX_CAMPAIGNS}&since_send_time=${startISO}&before_send_time=${endISO}&status=sent&sort_field=send_time&sort_dir=DESC`;
    const campaignsResult = await safeFetch(
      campaignsUrl,
      { method: "GET", headers },
      API_TIMEOUT_MS
    );

    if (!campaignsResult.success) {
      return adapterErr({
        code: campaignsResult.error.code,
        message: sanitizeErrorMessage(`Mailchimp campaigns fetch failed: ${campaignsResult.error.message}`),
        retryable: campaignsResult.error.retryable,
      });
    }

    if (!campaignsResult.data.ok) {
      const errorBody = await campaignsResult.data.json().catch(() => ({}));
      const parsed = parseMailchimpApiError(errorBody);
      return adapterErr({
        code: parsed?.type === "token_expired" ? "token_expired" : parsed?.type === "rate_limited" ? "rate_limited" : "invalid_response",
        message: sanitizeErrorMessage(parsed?.message || `Mailchimp API error: HTTP ${campaignsResult.data.status}`),
        retryable: parsed?.retryable ?? false,
      });
    }

    const campaignsData = (await campaignsResult.data.json()) as MailchimpCampaignsResponse;

    // ---- Fetch individual campaign reports (batched to respect 10-connection limit) ----
    // IMC-18: Cast to MailchimpFullCampaignReport to access industry_stats, forwards, ab_split
    const campaignReports: MailchimpFullCampaignReport[] = [];
    for (let i = 0; i < campaignsData.campaigns.length; i += BATCH_SIZE) {
      const batch = campaignsData.campaigns.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map((campaign) =>
          safeFetch(
            `${baseUrl}/reports/${campaign.id}`,
            { method: "GET", headers },
            API_TIMEOUT_MS
          )
        )
      );
      for (const reportResult of batchResults) {
        if (reportResult.success && reportResult.data.ok) {
          const report = (await reportResult.data.json()) as MailchimpFullCampaignReport;
          campaignReports.push(report);
        }
      }
    }

    // ---- Build campaign-to-type lookup from campaigns list ----
    const campaignTypeMap = new Map<string, MailchimpCampaign>();
    for (const campaign of campaignsData.campaigns) {
      campaignTypeMap.set(campaign.id, campaign);
    }

    // Log warning if campaigns were truncated by API limit
    if (campaignsData.total_items > MAX_CAMPAIGNS) {
      console.warn(`Mailchimp: ${campaignsData.total_items} campaigns in date range, capped at ${MAX_CAMPAIGNS}`);
    }

    // ---- Fetch A/B test sub-reports (separate sequential pass for variate campaigns only) ----
    const variateCampaignIds = campaignsData.campaigns
      .filter((c) => c.type === "variate")
      .map((c) => c.id);

    const subReportsMap = new Map<string, MailchimpCampaignReport[]>();
    for (let i = 0; i < variateCampaignIds.length; i += BATCH_SIZE) {
      const batch = variateCampaignIds.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map((campaignId) =>
          safeFetch(
            `${baseUrl}/reports/${campaignId}/sub-reports`,
            { method: "GET", headers },
            API_TIMEOUT_MS
          )
        )
      );
      for (let j = 0; j < batchResults.length; j++) {
        const reportResult = batchResults[j]!;
        if (reportResult.success && reportResult.data.ok) {
          const subData = (await reportResult.data.json()) as MailchimpSubReportsResponse;
          if (subData.campaigns && subData.campaigns.length > 0) {
            subReportsMap.set(batch[j]!, subData.campaigns);
          }
        }
        // 404 or empty → treat as regular campaign (no variant records)
      }
    }

    // ---- Fetch growth history (audience growth over time) ----
    let growthHistory: MailchimpGrowthHistoryEntry[] = [];
    if (audienceId) {
      const growthResult = await safeFetch(
        `${baseUrl}/lists/${audienceId}/growth-history?count=12&sort_field=month&sort_dir=DESC`,
        { method: "GET", headers },
        API_TIMEOUT_MS
      );
      if (growthResult.success && growthResult.data.ok) {
        const growthData = (await growthResult.data.json()) as MailchimpGrowthHistoryResponse;
        growthHistory = growthData.growth_history || [];
      }
    }

    // ======================================================================
    // IMC-18: Fetch expanded endpoints per campaign (batched)
    // ======================================================================

    // E-commerce per campaign
    const ecommerceMap = new Map<string, MailchimpEcommerceResponse>();
    const productActivityMap = new Map<string, MailchimpEcommerceProduct[]>();
    // Click details per campaign
    const clickDetailsMap = new Map<string, MailchimpClickDetailUrl[]>();
    // Domain performance per campaign
    const domainPerfMap = new Map<string, MailchimpDomainPerformanceEntry[]>();
    // Locations per campaign
    const locationsMap = new Map<string, MailchimpLocationEntry[]>();

    // Fetch expanded endpoints one campaign at a time (5 parallel calls per campaign).
    // Mailchimp allows 10 concurrent connections. Processing one campaign at a time
    // keeps us at 5 concurrent, leaving headroom for retries and other requests.
    for (const report of campaignReports) {
      const [ecomRes, prodRes, clickRes, domainRes, locRes] = await Promise.all([
        safeFetch(`${baseUrl}/reports/${report.id}/ecommerce`, { method: "GET", headers }, API_TIMEOUT_MS),
        safeFetch(`${baseUrl}/reports/${report.id}/ecommerce-product-activity?count=100`, { method: "GET", headers }, API_TIMEOUT_MS),
        safeFetch(`${baseUrl}/reports/${report.id}/click-details?count=100`, { method: "GET", headers }, API_TIMEOUT_MS),
        safeFetch(`${baseUrl}/reports/${report.id}/domain-performance`, { method: "GET", headers }, API_TIMEOUT_MS),
        safeFetch(`${baseUrl}/reports/${report.id}/locations?count=100`, { method: "GET", headers }, API_TIMEOUT_MS),
      ]);

      if (ecomRes.success && ecomRes.data.ok) {
        ecommerceMap.set(report.id, (await ecomRes.data.json()) as MailchimpEcommerceResponse);
      }
      if (prodRes.success && prodRes.data.ok) {
        const prodData = (await prodRes.data.json()) as MailchimpEcommerceProductResponse;
        if (prodData.products?.length) productActivityMap.set(report.id, prodData.products);
      }
      if (clickRes.success && clickRes.data.ok) {
        const clickData = (await clickRes.data.json()) as MailchimpClickDetailsResponse;
        if (clickData.urls_clicked?.length) clickDetailsMap.set(report.id, clickData.urls_clicked);
      }
      if (domainRes.success && domainRes.data.ok) {
        const domainData = (await domainRes.data.json()) as MailchimpDomainPerformanceResponse;
        if (domainData.domains?.length) domainPerfMap.set(report.id, domainData.domains);
      }
      if (locRes.success && locRes.data.ok) {
        const locData = (await locRes.data.json()) as MailchimpLocationsResponse;
        if (locData.locations?.length) locationsMap.set(report.id, locData.locations);
      }
    }

    // ---- Fetch automations ----
    let automations: MailchimpAutomation[] = [];
    const automationResult = await safeFetch(
      `${baseUrl}/automations?count=100`,
      { method: "GET", headers },
      API_TIMEOUT_MS
    );
    if (automationResult.success && automationResult.data.ok) {
      const autoData = (await automationResult.data.json()) as MailchimpAutomationsResponse;
      automations = autoData.automations || [];
    }

    // Fetch per-automation email steps (batched)
    const automationEmailsMap = new Map<string, MailchimpAutomationEmail[]>();
    for (let i = 0; i < automations.length; i += BATCH_SIZE) {
      const batch = automations.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.all(
        batch.map((auto) =>
          safeFetch(`${baseUrl}/automations/${auto.id}/emails`, { method: "GET", headers }, API_TIMEOUT_MS)
        )
      );
      for (let j = 0; j < batchResults.length; j++) {
        const res = batchResults[j]!;
        if (res.success && res.data.ok) {
          const emailsData = (await res.data.json()) as MailchimpAutomationEmailsResponse;
          if (emailsData.emails?.length) automationEmailsMap.set(batch[j]!.id, emailsData.emails);
        }
      }
    }

    // ---- Fetch list-level daily activity + subscriber locations ----
    let dailyActivity: MailchimpDailyActivityEntry[] = [];
    let listLocations: MailchimpListLocationEntry[] = [];
    if (audienceId) {
      const [activityRes, listLocRes] = await Promise.all([
        safeFetch(`${baseUrl}/lists/${audienceId}/activity?count=180`, { method: "GET", headers }, API_TIMEOUT_MS),
        safeFetch(`${baseUrl}/lists/${audienceId}/locations?count=100`, { method: "GET", headers }, API_TIMEOUT_MS),
      ]);
      if (activityRes.success && activityRes.data.ok) {
        const actData = (await activityRes.data.json()) as MailchimpDailyActivityResponse;
        dailyActivity = actData.activity || [];
      }
      if (listLocRes.success && listLocRes.data.ok) {
        const llData = (await listLocRes.data.json()) as MailchimpListLocationsResponse;
        listLocations = llData.locations || [];
      }
    }

    // ======================================================================
    // Aggregate metrics from campaign reports
    // ======================================================================

    let totalEmailsSent = 0;
    let totalOpens = 0;
    let totalUniqueOpens = 0;
    let totalClicks = 0;
    let totalUniqueClicks = 0;
    let totalHardBounces = 0;
    let totalSoftBounces = 0;
    let totalSyntaxErrors = 0;
    let totalUnsubscribed = 0;
    let totalAbuseReports = 0;
    let totalForwards = 0;
    let totalForwardOpens = 0;
    let totalSubscriberClicks = 0;
    // E-commerce aggregates
    let totalRevenue = 0;
    let totalOrders = 0;
    let totalSpent = 0;

    for (const report of campaignReports) {
      totalEmailsSent += report.emails_sent || 0;
      totalOpens += report.opens?.opens_total || 0;
      totalUniqueOpens += report.opens?.unique_opens || 0;
      totalClicks += report.clicks?.clicks_total || 0;
      totalUniqueClicks += report.clicks?.unique_clicks || 0;
      totalHardBounces += report.bounces?.hard_bounces || 0;
      totalSoftBounces += report.bounces?.soft_bounces || 0;
      totalSyntaxErrors += report.bounces?.syntax_errors || 0;
      totalUnsubscribed += report.unsubscribed || 0;
      totalAbuseReports += report.abuse_reports || 0;
      totalForwards += report.forwards?.forwards_count || 0;
      totalForwardOpens += report.forwards?.forwards_opens || 0;
      totalSubscriberClicks += report.clicks?.unique_subscriber_clicks || 0;

      // E-commerce per campaign
      const ecom = ecommerceMap.get(report.id);
      if (ecom) {
        totalRevenue += ecom.total_revenue || 0;
        totalOrders += ecom.total_orders || 0;
        totalSpent += ecom.total_spent || 0;
      }
    }

    const totalBounces = totalHardBounces + totalSoftBounces;
    const averageOpenRate = totalEmailsSent > 0
      ? totalUniqueOpens / totalEmailsSent
      : (listStats?.stats?.open_rate ?? 0);
    const averageClickRate = totalEmailsSent > 0
      ? totalUniqueClicks / totalEmailsSent
      : (listStats?.stats?.click_rate ?? 0);
    const bounceRate = totalEmailsSent > 0
      ? totalBounces / totalEmailsSent
      : 0;
    const unsubscribeRate = totalEmailsSent > 0
      ? totalUnsubscribed / totalEmailsSent
      : 0;
    const clickToOpenRate = totalUniqueOpens > 0
      ? totalUniqueClicks / totalUniqueOpens
      : 0;
    const deliveryRate = totalEmailsSent > 0
      ? (totalEmailsSent - totalBounces) / totalEmailsSent
      : 0;
    const abuseRate = totalEmailsSent > 0
      ? totalAbuseReports / totalEmailsSent
      : 0;

    // ---- Build daily overview from growth history ----
    const dailyOverview: Record<string, unknown>[] = growthHistory.map((entry) => ({
      date: entry.month,
      existingSubscribers: entry.existing,
      newSubscribers: entry.imports + entry.optins,
    }));

    // ---- Build campaign records (unsorted — widget config.sortBy handles display order) ----
    const campaignRecords: Record<string, unknown>[] = [];
    const variantRecords: Record<string, unknown>[] = [];
    // IMC-18: New record arrays
    const ecommerceRecords: Record<string, unknown>[] = [];
    const productActivityRecords: Record<string, unknown>[] = [];
    const clickMapRecords: Record<string, unknown>[] = [];
    const domainPerfRecords: Record<string, unknown>[] = [];
    const locationRecords: Record<string, unknown>[] = [];
    const benchmarkRecords: Record<string, unknown>[] = [];

    for (const report of campaignReports) {
      const campaign = campaignTypeMap.get(report.id);
      const campaignType = (campaign?.type ?? "regular") as "regular" | "variate" | "automation" | "rss";
      const isAbTest = campaignType === "variate";
      const recipientsCount = campaign?.recipients?.recipient_count ?? 0;
      const openCount = report.opens?.unique_opens ?? 0;
      const clickCount = report.clicks?.unique_clicks ?? 0;
      const campaignClickToOpenRate = openCount > 0 ? clickCount / openCount : 0;
      const delivered = report.emails_sent - (report.bounces?.hard_bounces ?? 0) - (report.bounces?.soft_bounces ?? 0);

      // Determine A/B winner label
      let abTestWinner: string | null = null;
      if (isAbTest) {
        const subReports = subReportsMap.get(report.id);
        const winningId = campaign?.variate_settings?.winning_campaign_id;
        if (winningId && subReports) {
          const winnerReport = subReports.find((sr) => sr.id === winningId);
          if (winnerReport) {
            const winnerIndex = subReports.indexOf(winnerReport);
            abTestWinner = `Variant ${String.fromCharCode(65 + winnerIndex)}`;
          }
        }

        // Build flat variant records from sub-reports
        if (subReports && subReports.length > 0) {
          for (let vi = 0; vi < subReports.length; vi++) {
            const variant = subReports[vi]!;
            const isWinner = !!winningId && variant.id === winningId;
            variantRecords.push({
              _reportType: "variant",
              parentCampaignId: report.id,
              variantId: variant.id,
              variantLabel: isWinner ? "Winner" : `Variant ${String.fromCharCode(65 + vi)}`,
              subjectLine: variant.subject_line || "",
              emailsSent: variant.emails_sent ?? 0,
              openRate: variant.opens?.open_rate ?? 0,
              clickRate: variant.clicks?.click_rate ?? 0,
              isWinner,
            });
          }
        }

        // IMC-18: Build detailed A/B variant records from ab_split ONLY if sub-reports
        // weren't available (to avoid duplicates — sub-reports and ab_split represent the same data)
        if (report.ab_split && (!subReports || subReports.length === 0)) {
          for (const variantKey of ["a", "b"] as const) {
            const v = report.ab_split[variantKey];
            if (v) {
              variantRecords.push({
                _reportType: "variant",
                parentCampaignId: report.id,
                variantId: `${report.id}-ab-${variantKey}`,
                variantLabel: `Variant ${variantKey.toUpperCase()}`,
                variant_opens: v.opens,
                variant_unique_opens: v.unique_opens,
                variant_clicks: v.recipient_clicks,
                variant_bounces: v.bounces,
                variant_unsubs: v.unsubs,
                variant_forwards: v.forwards,
              });
            }
          }
        }
      }

      campaignRecords.push({
        _reportType: "campaign",
        campaignId: report.id,
        campaignName: report.campaign_title || "Untitled",
        subjectLine: report.subject_line || "",
        sendDate: report.send_time,
        emailsSent: report.emails_sent,
        recipientsCount,
        openRate: report.opens?.open_rate ?? 0,
        openCount,
        clickRate: report.clicks?.click_rate ?? 0,
        clickCount,
        clickToOpenRate: campaignClickToOpenRate,
        bounceRate: report.emails_sent > 0
          ? ((report.bounces?.hard_bounces ?? 0) + (report.bounces?.soft_bounces ?? 0)) / report.emails_sent
          : 0,
        unsubscribes: report.unsubscribed,
        campaignType,
        isAbTest,
        abTestWinner,
        // IMC-18: expanded per-campaign fields
        subscriberClicks: report.clicks?.unique_subscriber_clicks ?? 0,
        abuseReports: report.abuse_reports ?? 0,
        syntaxErrors: report.bounces?.syntax_errors ?? 0,
        forwards: report.forwards?.forwards_count ?? 0,
        forwardOpens: report.forwards?.forwards_opens ?? 0,
        deliveryRate: report.emails_sent > 0
          ? (report.emails_sent - (report.bounces?.hard_bounces ?? 0) - (report.bounces?.soft_bounces ?? 0)) / report.emails_sent
          : 0,
        successfulDeliveries: delivered,
      });

      // IMC-18: E-commerce records
      const ecom = ecommerceMap.get(report.id);
      if (ecom) {
        const orderRate = delivered > 0 ? (ecom.total_orders / delivered) : 0;
        ecommerceRecords.push({
          _reportType: "ecommerce",
          campaignId: report.id,
          campaignName: report.campaign_title || "Untitled",
          revenue: ecom.total_revenue,
          orders: ecom.total_orders,
          avgOrderRevenue: ecom.average_order_revenue,
          totalSpent: ecom.total_spent,
          currencyCode: ecom.currency_code,
          orderRate,
        });
      }

      // IMC-18: Product activity records
      const products = productActivityMap.get(report.id);
      if (products) {
        for (const prod of products) {
          productActivityRecords.push({
            _reportType: "productActivity",
            campaignId: report.id,
            campaignName: report.campaign_title || "Untitled",
            product: prod.title,
            productSku: prod.sku,
            productRevenue: prod.total_revenue,
            productUnitsPurchased: prod.total_purchased,
            productRecommendationsPurchased: prod.recommendation_purchased,
          });
        }
      }

      // IMC-18: Click map records
      const clickUrls = clickDetailsMap.get(report.id);
      if (clickUrls) {
        for (const url of clickUrls) {
          clickMapRecords.push({
            _reportType: "clickMap",
            campaignId: report.id,
            campaignName: report.campaign_title || "Untitled",
            url: url.url,
            urlTotalClicks: url.total_clicks,
            urlUniqueClicks: url.unique_clicks,
            urlClickPercentage: url.click_percentage,
          });
        }
      }

      // IMC-18: Domain performance records
      const domains = domainPerfMap.get(report.id);
      if (domains) {
        for (const dom of domains) {
          domainPerfRecords.push({
            _reportType: "domainPerformance",
            campaignId: report.id,
            campaignName: report.campaign_title || "Untitled",
            emailDomain: dom.domain,
            domainEmailsSent: dom.emails_sent,
            domainOpens: dom.opens,
            domainClicks: dom.clicks,
            domainBounces: dom.bounces,
            domainDelivered: dom.delivered,
            domainUnsubs: dom.unsubs,
          });
        }
      }

      // IMC-18: Campaign location records
      const locs = locationsMap.get(report.id);
      if (locs) {
        for (const loc of locs) {
          locationRecords.push({
            _reportType: "locations",
            campaignId: report.id,
            campaignName: report.campaign_title || "Untitled",
            country: loc.country_code,
            region: loc.region,
            locationOpens: loc.opens,
            locationPercentOpens: loc.percent_opens ?? 0,
          });
        }
      }

      // IMC-18: Industry benchmark records
      if (report.industry_stats) {
        const stats = report.industry_stats;
        benchmarkRecords.push({
          _reportType: "benchmark",
          campaignId: report.id,
          campaignName: report.campaign_title || "Untitled",
          industryType: stats.type,
          industryOpenRate: stats.open_rate,
          industryClickRate: stats.click_rate,
          industryBounceRate: stats.bounce_rate,
          industryUnsubRate: stats.unsub_rate,
          industryAbuseRate: stats.abuse_rate,
          // Include campaign's own rates for comparison
          campaignOpenRate: report.opens?.open_rate ?? 0,
          campaignClickRate: report.clicks?.click_rate ?? 0,
        });
      }
    }

    // ---- IMC-18: Automation records ----
    const automationRecords: Record<string, unknown>[] = [];
    for (const auto of automations) {
      const rs = auto.report_summary;
      automationRecords.push({
        _reportType: "automation",
        automationId: auto.id,
        automationWorkflow: auto.settings?.title || "Untitled",
        emailsSent: auto.emails_sent ?? 0,
        automationOpens: rs?.opens ?? 0,
        automationUniqueOpens: rs?.unique_opens ?? 0,
        automationOpenRate: rs?.open_rate ?? 0,
        automationClicks: rs?.clicks ?? 0,
        automationSubscriberClicks: rs?.subscriber_clicks ?? 0,
        automationClickRate: rs?.click_rate ?? 0,
      });

      // Per-step records
      const steps = automationEmailsMap.get(auto.id);
      if (steps) {
        for (const step of steps) {
          const stepRs = step.report_summary;
          automationRecords.push({
            _reportType: "automationStep",
            automationId: auto.id,
            automationWorkflow: auto.settings?.title || "Untitled",
            automationStep: step.settings?.title || step.settings?.subject_line || `Step ${step.position}`,
            emailsSent: step.emails_sent ?? 0,
            automationOpens: stepRs?.opens ?? 0,
            automationUniqueOpens: stepRs?.unique_opens ?? 0,
            automationOpenRate: stepRs?.open_rate ?? 0,
            automationClicks: stepRs?.clicks ?? 0,
            automationSubscriberClicks: stepRs?.subscriber_clicks ?? 0,
            automationClickRate: stepRs?.click_rate ?? 0,
          });
        }
      }
    }

    // ---- IMC-18: Daily activity records ----
    const dailyActivityRecords: Record<string, unknown>[] = dailyActivity.map((entry) => ({
      _reportType: "dailyActivity",
      date: entry.day,
      dailyEmailsSent: entry.emails_sent,
      dailyUniqueOpens: entry.unique_opens,
      dailyRecipientClicks: entry.recipient_clicks,
      dailyHardBounces: entry.hard_bounces,
      dailySoftBounces: entry.soft_bounces,
      dailySubs: entry.subs,
      dailyUnsubs: entry.unsubs,
    }));

    // ---- IMC-18: Subscriber location records (list-level) ----
    const subscriberLocationRecords: Record<string, unknown>[] = listLocations.map((loc) => ({
      _reportType: "subscriberLocations",
      subscriberCountry: loc.country,
      subscriberCountryCode: loc.cc,
      subscriberPercent: loc.percent,
      subscriberTotal: loc.total,
    }));

    // ---- Build summary record ----
    const summaryRecord: Record<string, unknown> = {
      totalSubscribers: listStats?.stats?.member_count ?? 0,
      newSubscribers: growthHistory.length > 0
        ? growthHistory.reduce((sum, e) => sum + e.imports + e.optins, 0)
        : (listStats?.stats?.member_count_since_send ?? 0),
      unsubscribes: totalUnsubscribed || (listStats?.stats?.unsubscribe_count_since_send ?? 0),
      campaignsSent: campaignsData.campaigns.length,
      averageOpenRate,
      averageClickRate,
      clickToOpenRate,
      emailsSent: totalEmailsSent,
      uniqueOpens: totalUniqueOpens,
      totalOpens,
      uniqueClicks: totalUniqueClicks,
      totalClicks,
      bounceRate,
      hardBounces: totalHardBounces,
      softBounces: totalSoftBounces,
      unsubscribeRate,
      // IMC-18: expanded summary fields
      deliveryRate,
      successfulDeliveries: totalEmailsSent - totalBounces,
      abuseReports: totalAbuseReports,
      abuseRate,
      syntaxErrors: totalSyntaxErrors,
      subscriberClicks: totalSubscriberClicks,
      forwards: totalForwards,
      forwardOpens: totalForwardOpens,
      revenue: totalRevenue,
      orders: totalOrders,
      avgOrderRevenue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
      totalSpent: totalSpent,
      orderRate: (totalEmailsSent - totalBounces) > 0
        ? totalOrders / (totalEmailsSent - totalBounces)
        : 0,
      // Growth metrics from list stats + growth history
      unsubscribeCount: listStats?.stats?.unsubscribe_count ?? 0,
      cleanedCount: listStats?.stats?.cleaned_count ?? 0,
      listRating: listStats?.list_rating ?? 0,
      reconfirms: growthHistory.reduce((sum, e) => sum + (e.reconfirm || 0), 0),
      cleanedMonthly: growthHistory.reduce((sum, e) => sum + (e.cleaned || 0), 0),
      pending: growthHistory.reduce((sum, e) => sum + (e.pending || 0), 0),
    };

    const records: Record<string, unknown>[] = [
      { ...summaryRecord, _reportType: "summary" },
      ...campaignRecords,
      ...variantRecords,
      ...dailyOverview.map((d) => ({ ...d, _reportType: "dailyOverview" })),
      // IMC-18: new record types
      ...ecommerceRecords,
      ...productActivityRecords,
      ...clickMapRecords,
      ...domainPerfRecords,
      ...locationRecords,
      ...benchmarkRecords,
      ...automationRecords,
      ...dailyActivityRecords,
      ...subscriberLocationRecords,
    ];

    return adapterOk({
      platform: "mailchimp",
      accountId: audienceId || providerAccountId,
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    // Use metadata endpoint for health check (uses "OAuth" prefix, NOT "Bearer")
    const result = await safeFetch(
      "https://login.mailchimp.com/oauth2/metadata",
      {
        method: "GET",
        headers: {
          Authorization: `OAuth ${credentials.accessToken}`,
        },
      },
      10_000
    );

    if (!result.success) {
      return adapterErr({
        code: "network_error",
        message: sanitizeErrorMessage(`Mailchimp health check failed: ${result.error.message}`),
        retryable: true,
      });
    }

    if (result.data.status === 401) {
      return adapterErr({
        code: "token_expired",
        message: "Mailchimp token has been revoked",
        retryable: false,
      });
    }

    return adapterOk(result.data.ok);
  },

  /**
   * Catalog discovery (Story 31.9). Mailchimp is single-tenant
   * (`oauthGrantScope: "single-tenant"`, registry): one OAuth grant = one
   * Mailchimp account, so the result is always a length-1 array — the
   * account itself (from `GET /3.0/`, the API Root endpoint) IS the full
   * catalog. Audiences (lists) are a SUB-resource used for per-integration
   * attachment config (`integrations.account_id` stores an audience id via
   * the PUT route) — they are NOT grant resources and must never be
   * returned here (the pre-31.9 audiences-as-accounts behavior poisoned
   * the `oauth_grant_resources` catalog axis).
   *
   * The root endpoint is unpaginated and returns a single object — the
   * defensive dedupe-by-id rule (ADR-0004 §Anti-Patterns 4) holds by
   * construction (one emission).
   *
   * Error-code fidelity (AC #5): HTTP failures map per ./errors.ts
   * STATUS_CODE_MAP semantics so the 31-24 cron can distinguish revoked
   * tokens (401 → token_expired, NOT retryable — Mailchimp tokens are
   * permanent, 401 means REVOKED) from transient failures (429/5xx →
   * retryable). A 200 missing `account_id` stays an `invalid_response`
   * ERROR, never `adapterOk([])` — for a single-tenant platform the
   * tenant is always discoverable with a healthy token.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const providerAccountId = (ctx.env as unknown as Record<string, string>).providerAccountId || "";
    if (!providerAccountId.includes(":")) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid provider_account_id format",
        retryable: false,
      });
    }

    const { dc } = parseDcFromAccountId(providerAccountId);
    try {
      // SSRF guard (DC_PATTERN) — validates dc before any network call
      buildMailchimpApiBaseUrl(dc);
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: `Invalid Mailchimp data center prefix: "${dc}"`,
        retryable: false,
      });
    }

    const result = await fetchMailchimpAccountInfoWithDetail(
      credentials.accessToken,
      dc
    );

    if (!result.ok) {
      return adapterErr({
        code: result.code,
        message: sanitizeErrorMessage(
          `Mailchimp account discovery failed: ${result.message}`
        ),
        retryable: result.retryable,
        // Forward safeFetch's parsed Retry-After (set on real 429s) so the
        // 31-24 cron honors provider backoff instead of blind-retrying.
        retryAfterMs: result.retryAfterMs,
      });
    }

    const account = result.account;

    return adapterOk([
      {
        id: account.accountId,
        name: account.accountName,
        platform: "mailchimp" as const,
        // dc is operationally load-bearing (region-partitioned base URL);
        // email is the account contact. total_subscribers deliberately
        // excluded — volatile metrics belong in fetchMetrics, not the catalog.
        metadata: { dc: account.dc, email: account.email },
        // Mailchimp OAuth is scopeless — no `scope` param anywhere in the
        // flow; a token grants full-account access (see ./oauth.ts).
        // Explicit empty array (not omitted) so the catalog row mirrors
        // oauth_tokens.scopes, which the callback stores empty. Do NOT
        // invent pseudo-scopes (AC #2).
        scopes: [],
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return MAILCHIMP_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return MAILCHIMP_DIMENSIONS;
  },
};
